if (!customElements.get('grid-slider-component')) {
  class GridSliderComponent extends HTMLElement {
    #track = null;
    #inner = null;
    #arrows = null;
    #prevBtn = null;
    #nextBtn = null;
    #dots = null;
    #mqs = {};
    #onScroll = null;
    #onChange = null;

    connectedCallback() {
      this.#track = this.querySelector('[data-gs-ref="track"]');
      this.#inner = this.querySelector('[data-gs-ref="inner"]');
      this.#arrows = this.querySelector('[data-gs-ref="arrows"]');
      this.#prevBtn = this.querySelector('[data-gs-ref="prevBtn"]');
      this.#nextBtn = this.querySelector('[data-gs-ref="nextBtn"]');
      this.#dots = this.querySelector('[data-gs-ref="dots"]');

      if (!this.#track) return;

      this.#prevBtn?.addEventListener('click', () => this.#scrollBy('prev'));
      this.#nextBtn?.addEventListener('click', () => this.#scrollBy('next'));

      this.#onScroll = () => this.#updateButtons();
      this.#track.addEventListener('scroll', this.#onScroll, { passive: true });

      this.#mqs = {
        mobile: window.matchMedia('(max-width: 749px)'),
        tablet: window.matchMedia('(min-width: 750px) and (max-width: 1199px)'),
        desktop: window.matchMedia('(min-width: 1200px)'),
      };

      this.#onChange = () => this.#update();
      Object.values(this.#mqs).forEach((mq) => mq.addEventListener('change', this.#onChange));

      this.#update();
    }

    disconnectedCallback() {
      Object.values(this.#mqs).forEach((mq) => mq.removeEventListener('change', this.#onChange));
      this.#track?.removeEventListener('scroll', this.#onScroll);
    }

    #isSliderActive() {
      return (
        (this.#mqs.mobile?.matches && this.hasAttribute('mobile-slider')) ||
        (this.#mqs.tablet?.matches && this.hasAttribute('tablet-slider')) ||
        (this.#mqs.desktop?.matches && this.hasAttribute('desktop-slider'))
      );
    }

    #update() {
      const active = this.#isSliderActive();
      const hasGutters = this.hasAttribute('has-gutters');

      this.#track.classList.toggle('grid-slider__track--slider', active);
      this.#inner?.classList.toggle('grid-slider__inner--full-bleed', active && hasGutters);
      this.#arrows?.classList.toggle('grid-slider__arrows--visible', active);

      if (active) {
        this.#buildDots();
        this.#updateButtons();
      } else {
        // Snap back to start when switching from slider → grid
        this.#track.scrollLeft = 0;
        if (this.#prevBtn) this.#prevBtn.disabled = true;
        if (this.#nextBtn) this.#nextBtn.disabled = false;
        this.#dots?.classList.remove('grid-slider__dots--visible');
        if (this.#dots) this.#dots.replaceChildren();
      }
    }

    #scrollBy(direction) {
      // Scroll by one "page" — the visible width of the track
      const amount = this.#track.offsetWidth;
      this.#track.scrollBy({
        left: direction === 'next' ? amount : -amount,
        behavior: 'smooth',
      });
    }

    #updateButtons() {
      if (!this.#isSliderActive()) return;
      const { scrollLeft, scrollWidth, offsetWidth } = this.#track;
      if (this.#prevBtn) this.#prevBtn.disabled = scrollLeft <= 1;
      if (this.#nextBtn) this.#nextBtn.disabled = scrollLeft >= scrollWidth - offsetWidth - 1;
      this.#updateDots();
    }

    #buildDots() {
      if (!this.#dots) return;

      const { scrollWidth, offsetWidth } = this.#track;
      const pageCount = Math.max(1, Math.round(scrollWidth / offsetWidth));

      // Only show dots if there's more than one page to navigate
      if (pageCount <= 1) {
        this.#dots.classList.remove('grid-slider__dots--visible');
        this.#dots.replaceChildren();
        return;
      }

      // Rebuild only when the page count changes (breakpoint / resize)
      if (this.#dots.children.length !== pageCount) {
        const dots = Array.from({ length: pageCount }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'grid-slider__dot';
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', `${i + 1}`);
          dot.addEventListener('click', () => {
            this.#track.scrollTo({ left: this.#track.offsetWidth * i, behavior: 'smooth' });
          });
          return dot;
        });
        this.#dots.replaceChildren(...dots);
      }

      this.#dots.classList.add('grid-slider__dots--visible');
      this.#updateDots();
    }

    #updateDots() {
      if (!this.#dots || !this.#dots.children.length) return;
      const { scrollLeft, offsetWidth } = this.#track;
      const activeIndex = Math.round(scrollLeft / offsetWidth);
      Array.from(this.#dots.children).forEach((dot, i) => {
        const isActive = i === activeIndex;
        dot.classList.toggle('grid-slider__dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
  }

  customElements.define('grid-slider-component', GridSliderComponent);
}
